import ky from "ky";
import * as z from "zod";
import auth from "./auth";
import policies from "./policies";
import credentials from "@/lib/http/credentials";
import company from "@/lib/http/company";
import guardrails from "@/lib/http/guardrails";
import { env } from "@/env";
import securellm from "@/lib/http/securellm";
import gatewaykeys from "@/lib/http/gatewaykeys";
import piiFileScan from "@/lib/http/pii-file-scan";
import embedding from "@/lib/http/embedding";

const refreshTokenStorageKey = "polygraf_refresh_token" as const;
const tempTokenMarkerKey = "temp-token" as const;

type TokenRefreshResponse = {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
};

const urlSchema = z.url();
export async function getBackendUrl(): Promise<string> {
  const url = env.VITE_API_URL;
  if (url) return url;
  else {
    const override = await ky.get("/backend_url_override");
    const url = await override.text();

    const parseResult = urlSchema.safeParse(url);
    if (parseResult.success) return parseResult.data;
    else {
      console.error(z.treeifyError(parseResult.error));
      throw parseResult.error;
    }
  }
}

async function createClient() {
  // make the access_token variable non-global and close over it
  let accessToken: string | null = null;

  async function _refreshToken() {
    const refresh_token = localStorage.getItem(refreshTokenStorageKey);
    if (!refresh_token) return false;
    const tokensRes = await ky.post<TokenRefreshResponse>(
      "api/v1/auth/refresh",
      {
        prefixUrl: await getBackendUrl(),
        json: {
          refresh_token,
        },
        throwHttpErrors: false,
      },
    );

    if (!tokensRes.ok) {
      // Only drop the token when the server actually rejected it. A 429 from
      // the endpoint's rate limit, or a 5xx, is temporary -- throwing away a
      // still-valid refresh token there logs the user out for nothing.
      if (tokensRes.status === 401 || tokensRes.status === 403) {
        localStorage.removeItem(refreshTokenStorageKey);
      }
      return false;
    }
    const tokens = await tokensRes.json();

    localStorage.setItem(refreshTokenStorageKey, tokens.refresh_token);
    accessToken = tokens.access_token;
    return true;
  }

  // coalesce refresh calls
  let refreshing: null | Promise<boolean> = null;
  function refreshToken() {
    if (!refreshing) {
      refreshing = _refreshToken();
      refreshing.finally(() => void (refreshing = null));
    }
    return refreshing;
  }

  // The app must render even when the API is unreachable at start-up: a
  // network failure or timeout here is not a rejected token, so the refresh
  // token is kept and the router simply treats the session as signed out.
  let refreshed = false;
  try {
    refreshed = await refreshToken();
  } catch (err) {
    console.error("token refresh failed at start-up", err);
  }
  if (!refreshed) {
    sessionStorage.removeItem(tempTokenMarkerKey);
  }

  const client = ky.create({
    prefixUrl: await getBackendUrl(),
    hooks: {
      beforeRequest: [
        async (req) => {
          if (accessToken === null) {
            const refreshed = await refreshToken();
            if (!refreshed) return;
          }
          req.headers.set("Authorization", `Bearer ${accessToken}`);
        },
      ],
      afterResponse: [
        async (req, opts, res) => {
          if (res.status === 401) {
            // `refreshing` is cleared as soon as one refresh settles, so a
            // page that fires several requests at once would otherwise send a
            // separate refresh per 401 and burn through the rate limit. If the
            // token already moved on since this request went out, the 401 is
            // stale: just retry with the current token.
            const usedAuth = req.headers.get("Authorization");
            if (usedAuth === null || usedAuth === `Bearer ${accessToken}`) {
              await refreshToken();
            }
            req.headers.set("Authorization", `Bearer ${accessToken}`);

            // goes in an infinite loop if you call with `ky` for some reason
            return fetch(req, opts).then((r) => {
              if (
                r.status === 401 &&
                localStorage.getItem(refreshTokenStorageKey) !== null &&
                window.location.pathname !== "/auth/signin"
              ) {
                // force log out if it's still a 401
                localStorage.removeItem(refreshTokenStorageKey);
                window.location.pathname = "/auth/signin";
              }
            });
          }
        },
      ],
    },
  });

  function saveTokens(tokens: { access: string; refresh: string | null }) {
    accessToken = tokens.access;
    if (!accessToken && !refreshing) return false;
    if (tokens.refresh)
      localStorage.setItem(refreshTokenStorageKey, tokens.refresh);
    else {
      sessionStorage.setItem(tempTokenMarkerKey, "true");
    }
  }

  function logout() {
    accessToken = null;
    localStorage.removeItem(refreshTokenStorageKey);
    sessionStorage.removeItem(tempTokenMarkerKey);
  }

  function isLoggedIn(): boolean | "temp" {
    const refresh = localStorage.getItem(refreshTokenStorageKey);
    if (!refresh)
      return sessionStorage.getItem(tempTokenMarkerKey) === "true" && "temp";
    return Boolean(refresh);
  }

  return { client, saveTokens, logout, isLoggedIn };
}

const { client, saveTokens, logout, isLoggedIn } = await createClient();
export { saveTokens, logout, isLoggedIn };

export const api = {
  raw: client,
  auth,
  policies,
  credentials,
  company,
  guardrails,
  securellm,
  gatewaykeys,
  piiFileScan,
  embedding,
} as const;
